import React, { useState } from 'react';
import { Search, Loader2, ShieldCheck, EyeOff, AlertCircle, FileText } from 'lucide-react';
import { useLazyGetRachatsParClasseQuery } from '../../services/rachatApi';
import type { EtudiantRachatCommission, RachatNoteCommission } from '../../services/rachatApi';

const formatDate = (d: string) =>
  new Date(d).toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

const fmt = (n: number | null) => (n == null ? '—' : n.toFixed(2));

const VueCommission: React.FC = () => {
  const [classeCode, setClasseCode] = useState('');
  const [semestre, setSemestre] = useState<1 | 2>(1);
  const [annee, setAnnee] = useState('2025-2026');
  const [inclureAnnules, setInclureAnnules] = useState(false);
  const [searchEtudiant, setSearchEtudiant] = useState('');

  const [trigger, { data, isFetching, error }] = useLazyGetRachatsParClasseQuery();

  const handleConsulter = () => {
    if (!classeCode.trim()) return;
    trigger({
      classe_code: classeCode.trim().toUpperCase(),
      semestre,
      annee_academique: annee,
      inclure_annules: inclureAnnules,
    });
  };

  const etudiantsFiltres: EtudiantRachatCommission[] = (() => {
    if (!data?.etudiants) return [];
    if (!searchEtudiant) return data.etudiants;
    const q = searchEtudiant.toLowerCase();
    return data.etudiants.filter(
      (e) =>
        e.matricule.toLowerCase().includes(q) ||
        e.nom.toLowerCase().includes(q) ||
        e.prenoms.toLowerCase().includes(q),
    );
  })();

  return (
    <div className="space-y-5">
      {/* Bandeau confidentialité */}
      <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50">
        <ShieldCheck size={18} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
        <div className="text-xs text-amber-800 dark:text-amber-200">
          <div className="font-semibold mb-0.5">Vue confidentielle — Scolarité &amp; Commission du jury</div>
          <div className="flex items-center gap-1.5">
            <EyeOff size={11} />
            Ces informations ne figurent jamais sur les relevés des étudiants.
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Classe
            </label>
            <input
              type="text"
              value={classeCode}
              onChange={(e) => setClasseCode(e.target.value)}
              placeholder="L1MI"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 uppercase"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Semestre
            </label>
            <select
              value={semestre}
              onChange={(e) => setSemestre(Number(e.target.value) as 1 | 2)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value={1}>Semestre 1</option>
              <option value={2}>Semestre 2</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Année académique
            </label>
            <input
              type="text"
              value={annee}
              onChange={(e) => setAnnee(e.target.value)}
              placeholder="2025-2026"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              id="inclure-annules"
              type="checkbox"
              checked={inclureAnnules}
              onChange={(e) => setInclureAnnules(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            <label htmlFor="inclure-annules" className="text-xs text-gray-600 dark:text-gray-400">
              Inclure les rachats annulés
            </label>
          </div>
          <button
            onClick={handleConsulter}
            disabled={isFetching || !classeCode.trim()}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {isFetching ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
            Consulter
          </button>
        </div>
      </div>

      {/* Résultats */}
      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/50">
          <AlertCircle size={16} className="text-red-600 dark:text-red-400 mt-0.5" />
          <div className="text-xs text-red-700 dark:text-red-300">
            Erreur lors de la récupération des données. Vérifiez les paramètres.
          </div>
        </div>
      )}

      {data && (
        <>
          {/* Stats résumé */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Classe</div>
              <div className="text-xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                {data.classe_code} <span className="text-sm font-normal text-gray-500">— S{data.semestre}</span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{data.annee_academique}</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Étudiants concernés</div>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">
                {data.total_etudiants_avec_rachat}
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Notes rachetées</div>
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400 mt-1">
                {data.total_notes_rachetees}
              </div>
            </div>
          </div>

          {/* Recherche */}
          {data.etudiants.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <div className="relative max-w-sm">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchEtudiant}
                  onChange={(e) => setSearchEtudiant(e.target.value)}
                  placeholder="Rechercher un étudiant..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>
          )}

          {/* Liste des étudiants */}
          {etudiantsFiltres.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
              <ShieldCheck size={36} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {data.etudiants.length === 0
                  ? 'Aucune note rachetée pour cette classe et ce semestre.'
                  : 'Aucun résultat pour cette recherche.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {etudiantsFiltres.map((etud) => (
                <EtudiantCard key={etud.matricule} etudiant={etud} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const EtudiantCard: React.FC<{ etudiant: EtudiantRachatCommission }> = ({ etudiant }) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700/30 border-b border-gray-200 dark:border-gray-700">
        <div>
          <div className="font-semibold text-gray-900 dark:text-gray-100">
            {etudiant.nom.toUpperCase()} {etudiant.prenoms}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
            {etudiant.matricule}
          </div>
        </div>
        <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
          {etudiant.nb_notes_rachetees} note{etudiant.nb_notes_rachetees > 1 ? 's' : ''} rachetée{etudiant.nb_notes_rachetees > 1 ? 's' : ''}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 dark:bg-gray-700/20">
            <tr>
              <th className="px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300">UE</th>
              <th className="px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300">Matière</th>
              <th className="px-3 py-2 text-center font-semibold text-gray-600 dark:text-gray-300">Avant</th>
              <th className="px-3 py-2 text-center font-semibold text-gray-600 dark:text-gray-300">Après</th>
              <th className="px-3 py-2 text-center font-semibold text-gray-600 dark:text-gray-300">Note actuelle</th>
              <th className="px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300">Date</th>
              <th className="px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300">Par</th>
              <th className="px-3 py-2 text-center font-semibold text-gray-600 dark:text-gray-300">État</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {etudiant.rachats.map((r: RachatNoteCommission, idx: number) => (
              <tr key={idx} className={!r.rachat_actif ? 'opacity-60 bg-gray-50/50 dark:bg-gray-700/10' : ''}>
                <td className="px-3 py-2 font-mono text-gray-500 dark:text-gray-400">{r.ue_code || '—'}</td>
                <td className="px-3 py-2">
                  <div className="text-gray-800 dark:text-gray-200">{r.matiere_nom}</div>
                  <div className="text-[10px] text-gray-400 font-mono">{r.matiere_code}</div>
                </td>
                <td className="px-3 py-2 text-center font-mono text-gray-600 dark:text-gray-300">
                  {fmt(r.note_avant)}
                </td>
                <td className="px-3 py-2 text-center font-mono font-semibold text-green-600 dark:text-green-400">
                  {fmt(r.note_apres)}
                </td>
                <td className="px-3 py-2 text-center font-mono text-gray-700 dark:text-gray-300">
                  {fmt(r.note_examen_actuelle)}
                </td>
                <td className="px-3 py-2 text-[10px] text-gray-500 dark:text-gray-400">
                  {formatDate(r.date_application)}
                </td>
                <td className="px-3 py-2 text-[10px] text-gray-500 dark:text-gray-400">
                  {r.applique_par || '—'}
                </td>
                <td className="px-3 py-2 text-center">
                  {r.rachat_actif ? (
                    <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                      Actif
                    </span>
                  ) : (
                    <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                      Annulé
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VueCommission;
